import { rem } from 'polished'
import styled from 'styled-components'
import { AccommodationRoomTileImageContainer } from '../AccommodationRoomTileImage'
import type { PropsWithChildren } from 'react'
import { useIntl } from 'react-intl'
import clsx from 'clsx'
import { AccommodationIndividualSelectRoomTileDetailsContainer } from './AccommodationIndividualSelectRoomTileDetails'
import Sheet from 'components/utils/Sheet'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import LineArrowDownIcon from 'LuxKit/Icons/line/LineArrowDownIcon'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { AccommodationIndividualSelectRoomOptionsContainer } from './AccommodationIndividualSelectRoomOptions'

const CurrentSelectionBanner = styled.div`
  background-color: ${({ theme }) => theme.palette.highlight.primary.lightBackground};
  padding: ${rem(4)} ${rem(20)};
`

const AccommodationRoomTileGrid = styled(Sheet)`
  &.hasImage {
    display: grid;
    grid-template:
      "image" ${rem(210)}
      "details" auto
      "options" auto / 1fr;
    
    ${mediaQueryUp.tablet} {
      grid-template:
        "image details" auto
        "options options" auto / 40% auto;
    }
  }

  &:not(.hasImage) {
    display: grid;
    grid-template:
      "details" auto
      "options" auto / 1fr;

    > ${AccommodationRoomTileImageContainer} {
      display: none;
    }
  }

  ${mediaQueryUp.tablet} {
    border: 1px solid ${({ theme }) => theme.palette.neutral.surface.lightForeground};
  }

  > ${AccommodationRoomTileImageContainer} {
    grid-area: image;
    
    ${mediaQueryUp.tablet} {
      margin-top: -1px;
      margin-inline-start: -1px;
    }
  }
  > ${AccommodationIndividualSelectRoomTileDetailsContainer} {
    grid-area: details;
  }
  > ${AccommodationIndividualSelectRoomOptionsContainer} {
    grid-area: options;
  }
`

interface Props extends PropsWithChildren {
  roomPackage: App.HotelPackage | App.BedbankPackage | App.Property.Room
  isCurrentSelection?: boolean
  imageSize?: 'small' | 'medium'
}

function AccommodationIndividualSelectRoomTile(props: Props) {
  const { roomPackage, imageSize = 'medium', isCurrentSelection, children } = props

  const intl = useIntl()

  const room = 'roomType' in roomPackage ? roomPackage.roomType : roomPackage

  const currentSelectionText = intl.formatMessage({
    id: 'Beky72MfWnO7R7BetiiD',
    defaultMessage: 'Your current selection',
  })

  const tile = <AccommodationRoomTileGrid
    background="normal-background"
    className={clsx({ hasImage: !!room?.images?.length }, `image-size-${imageSize}`)}
  >
    {children}
  </AccommodationRoomTileGrid>

  if (isCurrentSelection) {
    return <div>
      <CurrentSelectionBanner>
        <CaptionTextBlock variant="large" weight="bold" startIcon={<LineArrowDownIcon />}>
          {currentSelectionText}
        </CaptionTextBlock>
      </CurrentSelectionBanner>
      {tile}
    </div>
  } else {
    return tile
  }
}

export default AccommodationIndividualSelectRoomTile
